import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';

export default function Contact() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d3tdg0jonp7ib57t37og', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Hero Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
              <i className="ri-customer-service-2-line text-cyan-400"></i>
              <span className="text-cyan-400 text-sm font-medium">24/7 Support Available</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6 bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Get in Touch
            </h1>
            <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              Have questions about PepCoin? Our dedicated support team is here to help you 24/7. 
              Choose your preferred way to reach us.
            </p>
          </div>

          {/* Quick Action Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <Link to="/help" className="group bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/20 rounded-2xl p-6 hover:border-cyan-500/40 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 rounded-xl bg-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-book-open-line text-cyan-400 text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-xl mb-2">Help Center</h3>
              <p className="text-gray-400 text-sm mb-4">Browse our comprehensive guides and tutorials</p>
              <div className="flex items-center gap-2 text-cyan-400 text-sm font-medium">
                <span>Explore Resources</span>
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </div>
            </Link>

            <Link to="/faq" className="group bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/20 rounded-2xl p-6 hover:border-purple-500/40 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-questionnaire-line text-purple-400 text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-xl mb-2">FAQ</h3>
              <p className="text-gray-400 text-sm mb-4">Find quick answers to common questions</p>
              <div className="flex items-center gap-2 text-purple-400 text-sm font-medium">
                <span>View FAQs</span>
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </div>
            </Link>

            <a href="https://t.me/+20f3SQXmKYZkZWU0" target="_blank" rel="noopener noreferrer" className="group bg-gradient-to-br from-pink-500/10 to-pink-500/5 border border-pink-500/20 rounded-2xl p-6 hover:border-pink-500/40 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 rounded-xl bg-pink-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-telegram-line text-pink-400 text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-xl mb-2">Live Chat</h3>
              <p className="text-gray-400 text-sm mb-4">Connect with our community on Telegram</p>
              <div className="flex items-center gap-2 text-pink-400 text-sm font-medium">
                <span>Join Community</span>
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </div>
            </a>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Form - Takes 2 columns */}
            <div className="lg:col-span-2">
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-10">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center">
                    <i className="ri-mail-send-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white">Send us a Message</h2>
                    <p className="text-gray-400 text-sm">We'll respond within 24 hours</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-300 mb-2 font-medium text-sm">
                        Full Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:bg-white/10 transition-all"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-300 mb-2 font-medium text-sm">
                        Email Address <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:bg-white/10 transition-all"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2 font-medium text-sm">
                      Subject <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      required
                      className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:bg-white/10 transition-all"
                      placeholder="How can we help you today?"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2 font-medium text-sm">
                      Message <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:bg-white/10 transition-all resize-none"
                      placeholder="Please describe your inquiry in detail..."
                    />
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-gray-500 text-xs">Maximum 500 characters</p>
                      <p className="text-gray-400 text-sm font-medium">
                        {formData.message.length}/500
                      </p>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-xl text-white font-bold text-lg hover:scale-[1.02] hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-3">
                        <i className="ri-loader-4-line animate-spin text-xl"></i>
                        Sending Message...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-3">
                        <i className="ri-send-plane-fill text-xl"></i>
                        Send Message
                      </span>
                    )}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 animate-fade-in">
                      <p className="text-green-300 flex items-center gap-3">
                        <i className="ri-checkbox-circle-fill text-xl"></i>
                        <span className="font-medium">Message sent successfully! We'll get back to you within 24 hours.</span>
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 animate-fade-in">
                      <p className="text-red-300 flex items-center gap-3">
                        <i className="ri-error-warning-fill text-xl"></i>
                        <span className="font-medium">Failed to send message. Please try again or contact us via email.</span>
                      </p>
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Sidebar - Takes 1 column */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-cyan-400"></i>
                  Contact Info
                </h3>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-cyan-400 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-2 text-sm">Email Support</h4>
                      <a href="mailto:teampepcoin@gmail.com" className="text-gray-400 hover:text-cyan-400 transition-colors text-sm block cursor-pointer">teampepcoin@gmail.com</a>
                      <a href="mailto:support@pepcoin.com" className="text-gray-400 hover:text-cyan-400 transition-colors text-sm block cursor-pointer">support@pepcoin.com</a>
                    </div>
                  </div>

                  <div className="h-px bg-white/10"></div>

                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-purple-400 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-2 text-sm">Support Hours</h4>
                      <p className="text-gray-400 text-sm">24/7 Available</p>
                      <p className="text-gray-400 text-sm">Response: &lt;24hrs</p>
                    </div>
                  </div>

                  <div className="h-px bg-white/10"></div>

                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-pink-400 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-2 text-sm">Follow Us</h4>
                      <div className="flex gap-2 mt-3">
                        <a href="https://twitter.com/pepcoin" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 hover:scale-110 transition-all cursor-pointer">
                          <i className="ri-twitter-x-line text-white text-sm"></i>
                        </a>
                        <a href="https://t.me/+20f3SQXmKYZkZWU0" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 hover:scale-110 transition-all cursor-pointer">
                          <i className="ri-telegram-line text-white text-sm"></i>
                        </a>
                        <a href="https://discord.gg/pepcoin" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 hover:scale-110 transition-all cursor-pointer">
                          <i className="ri-discord-line text-white text-sm"></i>
                        </a>
                        <a href="https://www.tiktok.com/@racetv7?_r=1&_t=ZN-91htxxc9vPi" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 hover:scale-110 transition-all cursor-pointer">
                          <i className="ri-tiktok-line text-white text-sm"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Resources */}
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <i className="ri-links-line text-cyan-400"></i>
                  Quick Links
                </h3>

                <div className="space-y-3">
                  <Link to="/faq" className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/20 transition-all cursor-pointer group">
                    <div className="flex items-center gap-3">
                      <i className="ri-question-line text-cyan-400"></i>
                      <span className="text-gray-300 text-sm font-medium">FAQ</span>
                    </div>
                    <i className="ri-arrow-right-line text-gray-500 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all"></i>
                  </Link>

                  <Link to="/help" className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/20 transition-all cursor-pointer group">
                    <div className="flex items-center gap-3">
                      <i className="ri-book-open-line text-purple-400"></i>
                      <span className="text-gray-300 text-sm font-medium">Help Center</span>
                    </div>
                    <i className="ri-arrow-right-line text-gray-500 group-hover:text-purple-400 group-hover:translate-x-1 transition-all"></i>
                  </Link>

                  <Link to="/kyc" className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/20 transition-all cursor-pointer group">
                    <div className="flex items-center gap-3">
                      <i className="ri-shield-check-line text-pink-400"></i>
                      <span className="text-gray-300 text-sm font-medium">KYC Verification</span>
                    </div>
                    <i className="ri-arrow-right-line text-gray-500 group-hover:text-pink-400 group-hover:translate-x-1 transition-all"></i>
                  </Link>

                  <Link to="/terms" className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/20 transition-all cursor-pointer group">
                    <div className="flex items-center gap-3">
                      <i className="ri-file-text-line text-orange-400"></i>
                      <span className="text-gray-300 text-sm font-medium">Terms & Conditions</span>
                    </div>
                    <i className="ri-arrow-right-line text-gray-500 group-hover:text-orange-400 group-hover:translate-x-1 transition-all"></i>
                  </Link>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-3xl p-6">
                <div className="flex items-start gap-3 mb-3">
                  <i className="ri-alarm-warning-line text-red-400 text-xl"></i>
                  <div>
                    <h4 className="text-white font-bold text-sm mb-1">Urgent Issue?</h4>
                    <p className="text-gray-400 text-xs leading-relaxed">
                      For critical security concerns or account emergencies, contact us immediately via email.
                    </p>
                  </div>
                </div>
                <a href="mailto:teampepcoin@gmail.com" className="block w-full py-2.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-lg text-red-300 text-sm font-medium text-center transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-mail-line mr-2"></i>
                  Email Support Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
